/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import moment from 'moment'
import { capitalize } from 'lodash'
import React from 'react'

import { EuiToolTip } from '@elastic/eui'

// eslint-disable-next-line import/no-restricted-paths
import { getLanguage } from '@/lib/locale'

import type { MomentInput } from 'moment'
import type { FunctionComponent } from 'react'

const longDay = `Do MMM, YYYY`
const longDayTime = `${longDay} HH:mm:ss ([UTC] Z)`

interface Props {
  date: MomentInput
  longTime?: boolean
  longFormat?: string
  shouldCapitalize?: boolean
  removeSuffix?: boolean
}

export const CuiTimeAgo: FunctionComponent<Props> = ({
  date,
  longTime = false,
  longFormat = null,
  shouldCapitalize = true,
  removeSuffix = false,
}) => {
  const when = moment(date)
  const relative = when.fromNow(removeSuffix)
  const capitalized = shouldCapitalize ? capitalize(relative) : relative

  const tooltipContent = when.format(longFormat || (longTime ? longDayTime : longDay))

  return (
    <span>
      <EuiToolTip position='right' content={tooltipContent}>
        <span>{capitalized}</span>
      </EuiToolTip>
    </span>
  )
}

interface CuiCombinedTimeProps {
  date: Date
  longTime?: boolean
  longFormat?: string
  shouldCapitalize?: boolean
  removeSuffix?: boolean
}

/**
 * This component shows a combined time, with the absolute date and the relative time from now.
 */
export const CuiCombinedTime: FunctionComponent<CuiCombinedTimeProps> = ({
  date,
  shouldCapitalize = true,
}) => {
  const fmt = new Intl.DateTimeFormat(getLanguage(window.navigator), {
    dateStyle: 'medium',
    timeStyle: 'long',
  })

  const relative = fromNow(date)

  const capitalized = shouldCapitalize ? capitalize(relative) : relative

  const absoluteDate = fmt.format(date)

  return (
    <span>
      {absoluteDate} ({capitalized})
    </span>
  )
}

/**
 * Shows a relative time from now in a localized, human-readable format.
 * TODO: we should use the `Intl.RelativeTimeFormat` API instead of moment.js in the future.
 * @param date the date relative to which to show a period relative to now
 * @param removeSuffix whether to remove the suffix (e.g. "in 5 minutes" -> "in 5 minutes" or "5 minutes ago" -> "5 minutes")
 * @returns the relative time from now
 */
export function fromNow(date: Date, removeSuffix = false): string {
  const when = moment(date)

  return when.fromNow(removeSuffix)
}

export function formatDate(
  date,
  {
    longTime = false,
  }: {
    longTime?: boolean
  } = {},
) {
  return moment(date).format(longTime ? longDayTime : longDay)
}
